import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Lock, ShieldCheck } from 'lucide-react';
import { useCart } from './CartContext';
import { useCurrency } from './CurrencyContext';
const IMAGEN_PRODUCTO_1 = 'https://cdn.magicpatterns.com/uploads/4av2VwMcsBAv8CuYbM2G3G/ChatGPT_Image_7_abr_2026,_09_52_28_a.m..png';
const PRICE_BASE = 6.95;
const PRICE_MEJORA = 9.95;
const PRICE_EXTRA = 2.97;
export function CartDrawer() {
  const {
    isCartOpen,
    closeCart
  } = useCart();
  const {
    currency,
    loading: loadingCurrency,
    formatPrice,
    currencyCopy
  } = useCurrency();
  const [timeLeft, setTimeLeft] = useState(15 * 60);
  const [mejoraSelected, setMejoraSelected] = useState(false);
  const [extraSelected, setExtraSelected] = useState(false);
  const toggleMejora = () => setMejoraSelected(!mejoraSelected);
  const toggleExtra = () => setExtraSelected(!extraSelected);
  // Dynamic total
  const totalUSD = PRICE_BASE + (mejoraSelected ? PRICE_MEJORA : 0) + (extraSelected ? PRICE_EXTRA : 0);
  // Dynamic checkout URL
  const getCheckoutUrl = () => {
    const items = ['42280601911381:1']; // Base product always included
    if (mejoraSelected) items.push('42391174611029:1');
    if (extraSelected) items.push('42391169335381:1');
    return `https://recetariosaludable.lat/cart/${items.join(',')}?checkout`;
  };
  useEffect(() => {
    if (!isCartOpen) return;
    const timer = setInterval(() => {
      setTimeLeft(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);
    return () => clearInterval(timer);
  }, [isCartOpen]);
  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };
  const handleCheckout = () => {
    let productName = 'Recetario Sin Gluten';
    const additions = [];
    if (mejoraSelected) additions.push('Mejora Completa');
    if (extraSelected) additions.push('Mejora Rápida');
    if (additions.length > 0) {
      productName += ' + ' + additions.join(' + ');
    }
    if (typeof window !== 'undefined' && (window as any).fbq) {
      ;
      (window as any).fbq('track', 'InitiateCheckout', {
        content_name: productName,
        content_type: 'product',
        value: totalUSD,
        currency: 'USD'
      });
    }
    window.open(getCheckoutUrl(), '_blank');
  };
  return <AnimatePresence data-id="element-61">
      {isCartOpen && <>
          {/* Overlay */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={closeCart} className="fixed inset-0 bg-black/60 z-[99998]" data-id="element-62" />

          {/* Drawer */}
          <motion.div initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} className="fixed right-0 top-0 h-full w-full md:max-w-[450px] bg-[#FDFBF7] text-[#101827] z-[99999] flex flex-col shadow-2xl font-[Montserrat,_sans-serif]" data-id="element-63">
            {/* HEADER */}
            <div className="flex justify-between items-center p-4 bg-white border-b border-[#E8D0B6]" data-id="element-64">
              <span className="font-extrabold text-lg tracking-tight" data-id="element-65">
                Tu Pedido
              </span>
              <button onClick={closeCart} className="p-1.5 hover:bg-gray-100 rounded-full transition-colors text-gray-500" data-id="element-66">
                <X className="w-5 h-5" data-id="element-67" />
              </button>
            </div>

            {/* TIMER */}
            <div className="text-center bg-[#FFD916]/20 py-2.5 text-[#101827] text-sm border-b border-[#FFD916]/30" data-id="element-68">
              <span className="font-medium" data-id="element-69">⏳ Esta oferta termina en</span>{' '}
              <span className="font-black text-[15px]" data-id="element-70">
                {formatTime(timeLeft)}
              </span>
              <p className="text-[10px] text-gray-600 font-semibold mt-0.5" data-id="element-71">
                👉 Después de esto, vuelve a precio normal
              </p>
            </div>

            {/* BODY */}
            <div className="p-4 flex-1 overflow-y-auto space-y-4" data-id="element-72">
              {/* ═══════════════════════════════════════════ */}
              {/* PRODUCTO BASE (siempre activo)             */}
              {/* ═══════════════════════════════════════════ */}
              <div className="bg-white border border-[#E8D0B6] p-4 rounded-xl shadow-sm relative overflow-hidden" data-id="element-73">
                <div className="absolute top-0 left-0 w-1 h-full bg-green-500" data-id="element-74"></div>
                <div className="flex gap-4" data-id="element-75">
                  <img src={IMAGEN_PRODUCTO_1} alt="Recetario Sin Gluten" className="w-20 h-24 object-cover rounded-lg shadow-sm" data-id="element-76" />
                  <div className="flex-1" data-id="element-77">
                    <div className="text-green-600 text-[11px] font-bold flex items-center gap-1 mb-1 uppercase tracking-wider" data-id="element-78">
                      <Check className="w-3.5 h-3.5" data-id="element-79" /> INCLUIDO EN TU PEDIDO
                    </div>
                    <h3 className="text-[15px] font-black leading-tight mb-1" data-id="element-80">
                      📘 Recetario Sin Gluten: 200+ Recetas Que Sí Funcionan
                    </h3>
                    <p className="text-[11px] text-gray-500 font-semibold mb-2" data-id="element-81">
                      Deja de fallar con recetas que no funcionan
                    </p>
                    <ul className="space-y-1 mb-2" data-id="element-82">
                      <li className="text-[11px] text-gray-600 flex items-start gap-1.5 leading-tight" data-id="element-83">
                        <Check className="w-3 h-3 text-green-500 flex-shrink-0 mt-0.5" data-id="element-84" />
                        Recetas probadas paso a paso
                      </li>
                      <li className="text-[11px] text-gray-600 flex items-start gap-1.5 leading-tight" data-id="element-85">
                        <Check className="w-3 h-3 text-green-500 flex-shrink-0 mt-0.5" data-id="element-86" />
                        Ingredientes fáciles de conseguir
                      </li>
                      <li className="text-[11px] text-gray-600 flex items-start gap-1.5 leading-tight" data-id="element-87">
                        <Check className="w-3 h-3 text-green-500 flex-shrink-0 mt-0.5" data-id="element-88" />
                        Resultados desde el primer intento
                      </li>
                    </ul>
                    <div className="font-black text-lg text-[#101827]" data-id="element-89">
                      {formatPrice(PRICE_BASE)}
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-center text-[12px] text-gray-500 font-semibold" data-id="element-90">
                Antes de continuar, asegúrate de no dejar esto fuera ↓
              </p>

              {/* ═══════════════════════════════════════════ */}
              {/* ORDER BUMP PRINCIPAL — $9.95                */}
              {/* ═══════════════════════════════════════════ */}
              <div onClick={toggleMejora} className={`block border-2 p-5 rounded-xl cursor-pointer transition-all relative overflow-hidden ${mejoraSelected ? 'border-[#FFD916] bg-[#FFFDF5] shadow-[0_6px_24px_rgba(0,0,0,0.1)]' : 'border-[#E8D0B6] bg-[#FFFEF8] hover:border-[#FFD916]/60 shadow-sm'}`} data-id="element-91">
                {/* Badge */}
                <div className="absolute top-0 right-0 bg-[#FFD916] text-[#101827] text-[10px] font-black px-3 py-1 rounded-bl-lg uppercase tracking-wider flex items-center gap-1" data-id="element-92">
                  ⭐ RECOMENDADO
                </div>

                {/* Product image */}
                <img src="https://cdn.magicpatterns.com/uploads/9u5NxKhpSLuxxMPKHYror7/ChatGPT_Image_23_abr_2026,_10_09_05_a.m..png" alt="Pack completo - Sistema de recetas sin gluten" className="w-full h-auto rounded-lg mb-3 mt-1" data-id="element-93" />

                {/* Title */}
                <h3 className="text-[15px] font-black leading-tight mb-2" data-id="element-94">
                  Evita errores desde el primer intento
                </h3>

                {/* Description */}
                <p className="text-[12px] text-gray-500 font-medium leading-relaxed mb-3" data-id="element-95">
                  Empieza con ventaja desde el día 1. Este complemento te
                  muestra exactamente cómo aplicar las recetas para que te
                  salgan bien desde el inicio, sin perder ingredientes ni
                  repetir intentos.
                </p>

                {/* Benefits */}
                <div className="space-y-2 mb-4" data-id="element-96">
                  <div className="flex items-start gap-2" data-id="element-97">
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-98" />
                    <span className="text-[13px] text-gray-800 font-semibold leading-snug" data-id="element-99">
                      Evita errores comunes desde el primer día
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-100">
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-101" />
                    <span className="text-[13px] text-gray-800 font-semibold leading-snug" data-id="element-102">
                      Ahorra tiempo y dinero en ingredientes
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-103">
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-104" />
                    <span className="text-[13px] text-gray-800 font-semibold leading-snug" data-id="element-105">
                      Sigue pasos claros sin improvisar
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-106">
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-107" />
                    <span className="text-[13px] text-gray-800 font-semibold leading-snug" data-id="element-108">
                      Resultados más rápidos y consistentes
                    </span>
                  </div>
                </div>

                {/* Price box */}
                <div className="bg-[#FFD916]/10 rounded-lg p-3 text-center border border-[#FFD916]/30 mb-3" data-id="element-109">
                  <p className="text-[18px] font-black text-[#101827]" data-id="element-110">
                    HOY SOLO {formatPrice(PRICE_MEJORA)}
                  </p>
                </div>

                {/* Social proof */}
                <p className="text-center text-[10px] text-[#D9A441] font-bold mb-3 tracking-wide" data-id="element-111">
                  ✔ Más del 60% de personas añade esta mejora
                </p>

                {/* Checkbox row */}
                <div className="flex items-center gap-3" data-id="element-112">
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors flex-shrink-0 ${mejoraSelected ? 'bg-[#FFD916] border-[#FFD916]' : 'border-gray-300 bg-white'}`} data-id="element-113">
                    {mejoraSelected && <Check className="w-3.5 h-3.5 text-[#101827]" data-id="element-114" />}
                  </div>
                  <span className="text-[12px] font-bold text-gray-700 leading-snug" data-id="element-115">
                    Sí, quiero hacerlo bien desde el inicio sin perder tiempo
                  </span>
                </div>
              </div>

              {/* Micro presión */}
              <p className="text-center text-[11px] text-gray-400 font-medium -mt-2" data-id="element-116">
                Sin esto, la mayoría tarda más en obtener resultados
              </p>

              {/* ═══════════════════════════════════════════ */}
              {/* EXTRA PEQUEÑO — $2.97                       */}
              {/* ═══════════════════════════════════════════ */}
              <div onClick={toggleExtra} className={`flex items-center gap-3 border rounded-lg px-4 py-3 cursor-pointer transition-all ${extraSelected ? 'border-[#FFD916] bg-[#FFD916]/5' : 'border-[#E8D0B6] bg-white hover:border-[#FFD916]/40'}`} data-id="element-117">
                <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors flex-shrink-0 ${extraSelected ? 'bg-[#FFD916] border-[#FFD916]' : 'border-gray-300 bg-white'}`} data-id="element-118">
                  {extraSelected && <Check className="w-3 h-3 text-[#101827]" data-id="element-119" />}
                </div>
                <span className="text-[12px] text-gray-600 font-semibold flex-1" data-id="element-120">
                  Recibe nuevas recetas, mejoras y contenido adicional sin
                  volver a pagar en el futuro
                </span>
                <span className="text-[13px] font-black text-[#101827]" data-id="element-121">
                  +{formatPrice(PRICE_EXTRA)}
                </span>
              </div>

              {/* Bottom spacer */}
              <div className="h-2" data-id="element-122"></div>
            </div>

            {/* FOOTER */}
            <div className="p-5 bg-white border-t border-[#E8D0B6] shadow-[0_-4px_20px_rgba(0,0,0,0.05)]" data-id="element-123">
              {/* Total */}
              <div className="flex justify-between items-center mb-4" data-id="element-124">
                <div className="flex flex-col" data-id="element-125">
                  <span className="text-gray-500 font-bold text-sm uppercase tracking-wider" data-id="element-126">
                    🔒 Total hoy:
                  </span>
                  <AnimatePresence mode="wait" data-id="element-127">
                    {mejoraSelected && <motion.span initial={{
                  opacity: 0,
                  y: -4
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -4
                }} className="inline-block mt-1.5 bg-green-100 text-green-700 text-[11px] font-bold px-2 py-1 rounded-md border border-green-200" data-id="element-128">
                        ✅ Acceso completo activado
                      </motion.span>}
                  </AnimatePresence>
                </div>
                <div className="text-right" data-id="element-129">
                  <AnimatePresence mode="wait" data-id="element-130">
                    <motion.span key={totalUSD} initial={{
                  scale: 0.8,
                  opacity: 0
                }} animate={{
                  scale: 1,
                  opacity: 1
                }} className="inline-block text-3xl font-black text-[#101827] leading-none" data-id="element-131">
                      {formatPrice(totalUSD)}
                    </motion.span>
                  </AnimatePresence>
                  {!loadingCurrency && currency.code !== 'USD' && <p className="text-[10px] text-gray-400 mt-1" data-id="element-132">
                      Aprox. ${totalUSD.toFixed(2)} USD
                    </p>}
                  {!loadingCurrency && <p className="text-[10px] text-green-600 font-semibold mt-1" data-id="element-133">
                      {currencyCopy}
                    </p>}
                </div>
              </div>

              {/* CTA Button */}
              <button onClick={handleCheckout} className="w-full bg-[#FFD916] hover:bg-[#f0cc15] text-[#101827] font-black text-lg py-4 rounded-xl shadow-[0_4px_14px_rgba(255,217,22,0.4)] hover:shadow-[0_6px_20px_rgba(255,217,22,0.5)] transition-all flex items-center justify-center gap-2 hover:-translate-y-0.5" data-id="element-134">
                <Lock className="w-5 h-5" data-id="element-135" />
                COMPLETAR COMPRA
              </button>

              {/* Sub-button text */}
              <p className="text-center text-[12px] text-gray-500 font-semibold mt-2.5" data-id="element-136">
                Acceso inmediato. Pago único.
              </p>

              {/* Trust triggers */}
              <div className="flex items-center justify-center gap-3 mt-3 text-[11px] text-gray-500 font-medium" data-id="element-137">
                <span className="flex items-center gap-1" data-id="element-138">
                  <Check className="w-3.5 h-3.5 text-green-500" data-id="element-139" /> Acceso
                  inmediato
                </span>
                <span className="flex items-center gap-1" data-id="element-140">
                  <ShieldCheck className="w-3.5 h-3.5 text-green-500" data-id="element-141" /> Pago
                  100% seguro
                </span>
                <span className="flex items-center gap-1" data-id="element-142">
                  <Check className="w-3.5 h-3.5 text-green-500" data-id="element-143" /> Garantía 7
                  días
                </span>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}